import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import AgreementAcceptModal from "../components/AgreementAcceptModal";
import BillOfSaleAcceptModal from "../components/BillOfSaleAcceptModal";
import { api } from "../lib/api";
import { getAgreementStatus } from "../lib/apiAgreements";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");

const BUYER_AGREEMENT_CONFIG = {
  agreementCode: "BUYER_PLATFORM",
  title: "Buyer Platform Agreement",
  workflowContext: { workflow: "artwork_purchase" },
};

export function useArtworkPurchaseFlow(defaultArtworkId, defaultHostId) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingArtworkId, setPendingArtworkId] = useState(defaultArtworkId || null);
  const [pendingHostId, setPendingHostId] = useState(defaultHostId ?? null);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [showBillOfSaleModal, setShowBillOfSaleModal] = useState(false);

  const redirectToCheckout = async (artworkId, hostId) => {
    await api.ensureCsrf();
    const payload = { artwork_id: artworkId };
    if (hostId != null) payload.host_id = hostId;

    const { id: sessionId } = await api.postJson("/payments/create-checkout-session/", payload);
    if (!sessionId) throw new Error("No session id returned");

    const stripe = await stripePromise;
    const { error: stripeError } = await stripe.redirectToCheckout({ sessionId });
    if (stripeError) {
      throw new Error(stripeError.message || "Unable to redirect to checkout.");
    }
  };

  const startArtworkPurchaseFlow = async (artworkId = defaultArtworkId, hostId = defaultHostId) => {
    if (!artworkId) return;

    setPendingArtworkId(artworkId);
    setPendingHostId(hostId ?? null);
    setLoading(true);
    setError("");

    try {
      const agreementStatus = await getAgreementStatus([BUYER_AGREEMENT_CONFIG.agreementCode]);
      const agreementRow = agreementStatus.results?.find(
        (item) => item.code === BUYER_AGREEMENT_CONFIG.agreementCode
      );

      if (agreementRow && !agreementRow.accepted) {
        setShowAgreementModal(true);
        return;
      }

      setShowBillOfSaleModal(true);
    } catch (err) {
      const detail = err?.payload?.detail || err?.message || "Unable to start checkout.";
      setError(detail);
      alert(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckoutFailure = async (err) => {
    const code = err?.payload?.code;
    const detail = err?.payload?.detail || err?.message || "Unable to start checkout.";
    setError(detail);

    if (code === "buyer_agreement_required") {
      setShowAgreementModal(true);
      return;
    }
    if (code === "bill_of_sale_required") {
      setShowBillOfSaleModal(true);
      return;
    }
    alert(detail);
  };

  const continueToCheckout = async () => {
    if (!pendingArtworkId) return;
    setLoading(true);
    setError("");
    try {
      await redirectToCheckout(pendingArtworkId, pendingHostId);
    } catch (err) {
      await handleCheckoutFailure(err);
    } finally {
      setLoading(false);
    }
  };

  const modalNodes = (
    <>
      <AgreementAcceptModal
        open={showAgreementModal}
        onClose={() => setShowAgreementModal(false)}
        agreementCode={BUYER_AGREEMENT_CONFIG.agreementCode}
        title={BUYER_AGREEMENT_CONFIG.title}
        workflowContext={BUYER_AGREEMENT_CONFIG.workflowContext}
        onAccepted={async () => {
          setShowAgreementModal(false);
          setShowBillOfSaleModal(true);
        }}
      />
      <BillOfSaleAcceptModal
        open={showBillOfSaleModal}
        artworkId={pendingArtworkId}
        onClose={() => setShowBillOfSaleModal(false)}
        onAccepted={async () => {
          setShowBillOfSaleModal(false);
          await continueToCheckout();
        }}
      />
    </>
  );

  return {
    startArtworkPurchaseFlow,
    loading,
    error,
    modalNodes,
  };
}
